import ProductSlider from "@/components/Ui/Pages/ProductSlider/ProductSlider";
import { baseURL } from "@/services/api";
import { IRelateds } from "global-types/global.types";
import { GetStaticPaths, GetStaticProps } from "next";
import styled from "styled-components";
import "bootstrap/dist/css/bootstrap.min.css";
import Link from "next/link";
import { t, localePrefix } from "@/helper/helper";
import Image from "next/image";
import axios from "axios";
import { SITE_NAME, SITE_URL } from "@/helper/constant";
import { NextSeo } from "next-seo";
import { useRouter } from "next/router";
import Head from "next/head";

export interface IBrandsDetailGeneric {
  about: string;
  id: number;
  logo: string;
  name: string;
  products: IRelateds[];
  slug: string;
  website: string;
  meta_desc: string;
  og_img: string;
  banner: string;
  meta_title: string | null;
}

export interface IBrandsDetail<T> {
  data: T;
}

interface Props {
  data: IBrandsDetail<IBrandsDetailGeneric>;
  locale: string;
}

export default function BrandsDetail({ locale, data }: Props) {
  const { asPath } = useRouter();

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: t("homePage", locale),
        item: `${SITE_URL}${localePrefix(locale)}`,
      },
      {
        "@type": "ListItem",
        position: 2,
        name: t("brands", locale),
        item: `${SITE_URL}${localePrefix(locale)}/brand`,
      },
      {
        "@type": "ListItem",
        position: 3,
        name: data.data.name,
        item: `${SITE_URL}${localePrefix(locale)}/brand/${data.data.slug}`,
      },
    ],
  };

  return (
    <>
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
        />
      </Head>
      <NextSeo
        title={data.data.meta_title || ""}
        description={data?.data?.meta_desc.substring(0, 160) || ""}
        canonical={SITE_URL + localePrefix(locale) + asPath}
        additionalMetaTags={[
          {
            name: "title",
            content: data?.data?.meta_title || "",
          },
        ]}
        openGraph={{
          url: SITE_URL + localePrefix(locale) + asPath,
          title: data?.data?.name + " " + SITE_NAME,
          description: data?.data?.meta_desc.substring(0, 300),
          images: [
            {
              url: data?.data?.og_img,
            },
          ],
          siteName: SITE_NAME,
        }}
      />
      <Wrapper>
        <div className="brand">
          <div className="breadcrumb">
            <Link href={`${localePrefix(locale) || "/"}`}>{t("homePage", locale)}</Link>
            <span className="breadcrumb-seperator" />
            <Link href={`${localePrefix(locale)}/brand/${data.data.slug}`}>
              {data.data.name}
            </Link>
          </div>
          <div className="brand-content">
            <div
              className="brand-header"
              style={{
                backgroundImage: `url(${data.data.banner})`,
              }}
            >
              <div className="brand-img">
                <Image
                  width={200}
                  height={200}
                  src={`${data.data.logo}`}
                  alt=""
                />
              </div>
            </div>
            <div className="brand-about">
              <h1>
                {data.data.website ? (
                  <a href={data.data.website} target={"_blank"}>
                    {data.data.name}
                  </a>
                ) : (
                  <span>{data.data.name}</span>
                )}
              </h1>
            </div>

            <ProductSlider
              locale={locale}
              title={t("allProducts", locale)}
              data={data.data.products}
              slug={""}
            />
            <div
              className="brand-about-child"
              dangerouslySetInnerHTML={{
                __html: data.data.about,
              }}
            ></div>
          </div>
        </div>
      </Wrapper>
    </>
  );
}

export const getStaticPaths: GetStaticPaths = async () => {
  const brandData = await fetch(baseURL + "/brand");
  const brandDataJson = await brandData.json();

  const locales = ["en", "ru", "az"];

  const paths = brandDataJson.data
    .slice(0, 10)
    .flatMap((brand: IBrandsDetailGeneric) =>
      locales.map((locale) => ({
        params: { slug: brand.slug },
        locale: locale,
      })),
    );

  return {
    paths,
    fallback: "blocking",
  };
};

export const getStaticProps: GetStaticProps = async (ctx) => {
  const { locale } = ctx;
  const { slug } = ctx.params!;

  try {
    const brandDetailData = await axios.get(baseURL + `/brand/${slug}`, {
      headers: {
        "x-localization": locale || "az",
      },
    });

    return {
      props: {
        data: brandDetailData.data,
        locale,
        slug,
      },
      revalidate: 60,
    };
  } catch (error) {
    return {
      notFound: true,
    };
  }
};

const Wrapper = styled.div`
  .brand {
    width: 1128px;
    margin: 0 auto;
    padding-bottom: 7rem;

    .wrap-col-brand {
      height: 200px;
      display: flex;
      flex-direction: column;

      a {
        display: flex;
        flex-direction: column;
        align-items: center;
        width: 100%;
        font-size: 17px;
        color: #1b1c1f;
      }

      img {
        width: 100px;
      }
    }

    @media screen and (max-width: 1199px) {
      width: 100%;
      padding: 0 16px;
    }

    .brand-header {
      background-repeat: no-repeat;
      background-size: cover;
      background-position: center center;
      width: 100%;
      height: 300px;
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 3.2rem;
      position: relative;

      .brand-img {
        position: relative;
        z-index: 2;

        img {
          width: 150px;
          height: 150px;
          object-fit: contain;
          filter: brightness(1.3) drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3));
        }
      }
    }

    .tabs {
      cursor: pointer;
      text-align: center;

      .tab {
        font-style: normal;
        font-weight: normal;
        font-size: 1.8rem;
        line-height: 100%;
        color: rgba(27, 28, 31, 0.25);
        text-align: center;
        width: 30%;
        display: inline-grid;
        // border-top: .1rem solid rgba(184, 190, 206, 0.25);
        // border-bottom: .1rem solid rgba(184, 190, 206, 0.25);
        padding: 1.4rem 0;

        &.active {
          color: #1b1c1f;
        }

        // &:nth-child(1) {
        //     text-align: right;
        // }

        // &:nth-child(2) {
        //     text-align: left;
        // }
      }
    }

    .about-content {
      margin-top: 36px;
      width: 812px;
      margin: auto;

      h5 {
        font-style: normal;
        font-weight: bold;
        font-size: 36px;
        line-height: 150%;
        color: #040941;
        margin-bottom: 24px;
      }

      p {
        font-style: normal;
        font-weight: 500;
        font-size: 20px;
        line-height: 30px;
        color: #989eaf;
      }
    }

    // .breadcrumb {
    //     display: none;
    // }

    .about-header {
      background: url("../../assets//img/account-bg.png");
      background-repeat: no-repeat;
      background-size: cover;
      background-position: center center;
      width: 100%;
      height: 27rem;
      border-radius: 6px;
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      justify-content: space-between;
      padding: 2.4rem;
      margin-bottom: 5.6rem;
      margin-top: 24px;

      .breadcrumb {
        padding-top: 0;
        display: block;

        a {
          color: #fff;
        }

        .breadcrumb-seperator {
          background-color: #fff;
        }
      }
    }

    .brand-content {
      margin-top: 5.6rem;

      .content {
        margin-top: 3.6rem;
        display: grid;
      }

      .brand-about-child {
        font-style: normal;
        font-weight: 400;
        font-size: 2rem;
        line-height: 30px;
        color: #989eaf;
        text-align: left;
        margin-bottom: 30px;
      }

      .brand-about {
        width: 812px;
        margin: auto;
        margin-bottom: 4.8rem;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;

        h1 {
          a,
          span {
            font-style: normal;
            font-weight: bold;
            font-size: 36px;
            line-height: 150%;
            color: #040941;
            margin-bottom: 2.4rem;
          }
        }

        p {
          font-style: normal;
          font-weight: 500;
          font-size: 20px;
          line-height: 30px;
          color: #989eaf;
        }

        a {
          font-style: normal;
          font-weight: normal;
          font-size: 16px;
          line-height: 100%;
          /* identical to box height, or 16px */

          letter-spacing: 0.02em;
          color: #444444;
        }
      }
    }

    .brands-products {
      grid-column: 1/-1;
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
      grid-gap: 2.4rem 1.6rem;
      align-content: flex-start;
    }
  }

  @media screen and (max-width: 992.9px) {
    .brand .brand-content .brand-about {
      width: 100%;
    }
  }

  @media screen and (max-width: 767.9px) {
    .brand {
      .brand-content {
        margin-top: 24px;

        .brand-about {
          width: 100%;
          margin-bottom: 24px;

          h5 {
            font-size: 2.8rem;
            margin-bottom: 2.4rem;
          }

          p {
            font-size: 1.6rem;
            font-weight: 500;
            line-height: 150%;
          }
        }
      }

      .brand-header {
        height: 200px;
        margin-bottom: 2.4rem;

        .brand-img img {
          width: 100px;
          height: 100px;
        }
      }

      .tabs {
        .tab {
          width: 50%;
          font-size: 1.6rem;
        }
      }
    }
  }
`;
